import { useCallback } from "react";
import { parseApiError } from "@/lib/error-handling";
import { buildUpdateEventPayloadFromEvent } from "@/lib/event-utils";
import { useAdminEventUpdate } from "@/lib/admin-api";
import type { ToastMessage } from "@/lib/ui";
import type { AdminEvent } from "../types";

type UseEventStateArgs = {
  event: AdminEvent | null;
  eventId: string | undefined;
  refreshEvent: () => Promise<void>;
  showToast: (message: ToastMessage) => void;
};

export function useEventState(args: UseEventStateArgs) {
  const { event, eventId, refreshEvent, showToast } = args;
  const updateMutation = useAdminEventUpdate();

  const handlePublish = useCallback(async () => {
    if (!event || !eventId) return;

    try {
      const payload = buildUpdateEventPayloadFromEvent(event, {
        isPublished: true,
      });

      try {
        await updateMutation.mutateAsync({
          params: { path: { eventId } },
          body: payload,
        });
      } catch (apiError: unknown) {
        const { message } = parseApiError(apiError);
        const errorMessage = message || "Failed to publish event";

        if (errorMessage.includes("image")) {
          showToast({
            severity: "error",
            summary: "Cannot Publish Event",
            detail:
              "Events must have an image before they can be published. Please upload an image first.",
            life: 5000,
          });
        } else {
          showToast({
            severity: "error",
            summary: "Error",
            detail: errorMessage,
          });
        }
        return;
      }

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Event published successfully",
      });

      await refreshEvent();
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : "Failed to publish event",
      });
    }
  }, [event, eventId, refreshEvent, showToast, updateMutation]);

  const handleUnpublish = useCallback(async () => {
    if (!event || !eventId) return;

    try {
      const payload = buildUpdateEventPayloadFromEvent(event, {
        isPublished: false,
      });

      await updateMutation.mutateAsync({
        params: { path: { eventId } },
        body: payload,
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Event unpublished successfully",
      });

      await refreshEvent();
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail:
          error instanceof Error ? error.message : "Failed to unpublish event",
      });
    }
  }, [event, eventId, refreshEvent, showToast, updateMutation]);

  const handleCancel = useCallback(async () => {
    if (!event || !eventId) return;

    try {
      const payload = buildUpdateEventPayloadFromEvent(event, {
        isCancelled: true,
      });

      await updateMutation.mutateAsync({
        params: { path: { eventId } },
        body: payload,
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Event cancelled successfully",
      });

      await refreshEvent();
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : "Failed to cancel event",
      });
    }
  }, [event, eventId, refreshEvent, showToast, updateMutation]);

  const handleUncancel = useCallback(async () => {
    if (!event || !eventId) return;

    try {
      const payload = buildUpdateEventPayloadFromEvent(event, {
        isCancelled: false,
      });

      await updateMutation.mutateAsync({
        params: { path: { eventId } },
        body: payload,
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Event uncancelled successfully",
      });

      await refreshEvent();
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail:
          error instanceof Error ? error.message : "Failed to uncancel event",
      });
    }
  }, [event, eventId, refreshEvent, showToast, updateMutation]);

  return {
    handlePublish,
    handleUnpublish,
    handleCancel,
    handleUncancel,
  };
}







